import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LandLordContext from "../../context/landlord/LandLordContext";
import useTranslator from "../../hooks/useTranslator";

const Row = ({ title, subtitle, selected, disabled, badge, onClick }) => (
  <div
    onClick={disabled ? undefined : onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "10px 14px",
      borderRadius: 20,
      border: selected && !disabled ? "1px solid #1a2e2a" : "1px solid #e8e8ef",
      background: disabled ? "#f2f2f2" : selected ? "#fafafb" : "#fff",
      cursor: disabled ? "default" : "pointer",
    }}
  >
    <span
      style={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
        background: disabled || selected ? "#f0f7e6" : "#fff",
        border: `1.5px solid ${selected || disabled ? "#d6e8c8" : "#e0e0e8"}`,
        color: "#1a2e2a",
        fontSize: 11,
        flexShrink: 0,
      }}
    >
      {selected || disabled ? "✓" : ""}
    </span>
    <div style={{ flex: 1 }}>
      <div
        style={{
          fontWeight: 700,
          color: "#1a2e2a",
          fontSize: 13,
          lineHeight: 1.3,
        }}
      >
        {title}
      </div>
      {subtitle && (
        <div
          style={{
            color: "#5a5a6a",
            fontSize: 12,
            lineHeight: 1.4,
            marginTop: 2,
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
    {badge && (
      <span
        style={{
          color: "#9aa0a8",
          fontSize: 13,
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {badge}
      </span>
    )}
  </div>
);

const AdditionalChecksModal = ({
  user,
  step,
  additionalInfo,
  setAdditionalInfo,
  formHandler,
  updateUserProfile,
  getNextStep,
}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { updateAdditionalInfo } = useContext(LandLordContext);
  const [loading, setLoading] = useState(false);
  const hasKyc = additionalInfo.includes("TENANT_KYC");
  const hasSkor =
    additionalInfo.includes("TENANT_SKOR") ||
    additionalInfo.includes("GUARANTOR_SKOR");

  const toggle = (key) =>
    setAdditionalInfo((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const toggleFinancialStudy = () => {
    setAdditionalInfo((prev) => {
      const selected = prev.includes("TENANT_SKOR");

      if (selected) {
        return prev.filter(
          (key) => !["TENANT_SKOR", "GUARANTOR_SKOR"].includes(key)
        );
      }

      return [...prev.filter((key) => key !== "GUARANTOR_SKOR"), "TENANT_SKOR"];
    });
  };

  const STEP_TO_NUM = {
    checks: 1,
    basic: 2,
    identity: 3,
    documents: 5,
    financial: 6,
  };
  const handleNext = async () => {
    try {
      setLoading(true);
      const _stepId = getNextStep(step);
      const _step = _stepId ? (STEP_TO_NUM[_stepId] ?? _stepId) : null;
      if (user?._id) {
        await updateAdditionalInfo({
          id: user._id,
          additional_info: additionalInfo,
        });
        if (_step) await updateUserProfile({ id: user._id, step: _step });
      }
      formHandler.next();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="popup-overlay wrapper-modal"
      style={{
        background: "var(--skor-color-canvas)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "0 28px",
        overflowY: "visible",
        height: "auto",
        minHeight: "auto",
      }}
    >
      <div
        className="tenant-checks-card"
        style={{
          width: "min(640px, 100%)",
          maxWidth: 640,
          margin: "clamp(32px, 7vh, 80px) 0 clamp(24px, 3.5vh, 40px)",
          padding: 34,
          border: "1px solid var(--skor-color-border-card)",
          borderRadius: 22,
          background: "var(--skor-color-surface)",
          boxShadow: "0 18px 50px rgba(37,56,55,0.09)",
        }}
      >
        <button
          type="button"
          onClick={() => navigate(`/${workspaceId}/created-tenant`)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            margin: "0 0 24px",
            padding: 0,
            border: 0,
            background: "transparent",
            color: "var(--skor-color-text-secondary)",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>
            ‹
          </span>
          {_t("back")}
        </button>
        <h1
          style={{
            fontSize: 25,
            fontWeight: 700,
            letterSpacing: "-0.025em",
            lineHeight: 1.1,
            color: "var(--skor-color-primary)",
            margin: "0 0 10px",
          }}
        >
          {_t("whatDoYouWantToCheck", "What do you want to check?")}
        </h1>
        <p
          style={{
            color: "var(--skor-color-text-secondary)",
            fontSize: 14,
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          {_t(
            "checksSubtitle",
            "Basic data and document analysis are always included. Add more checks if you need them."
          )}
        </p>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            marginTop: 20,
          }}
        >
          <Row
            title={_t("basicData", "Basic data")}
            badge={_t("alwaysIncluded", "Always included")}
            selected
            disabled
          />
          <Row
            title={_t("documentAnalysis", "Document analysis")}
            badge={_t("alwaysIncluded", "Always included")}
            selected
            disabled
          />
          <Row
            title={_t(
              "identityVerificationId",
              "Identity verification (ID document)"
            )}
            subtitle={_t(
              "verifyIdentitySubtitle",
              "Verify their identity with a photo of their ID document"
            )}
            selected={hasKyc}
            onClick={() => toggle("TENANT_KYC")}
          />
          <Row
            title={_t("financialStudy", "Financial study")}
            subtitle={_t(
              "financialStudySubtitle",
              "Assess their income with a bank statement"
            )}
            selected={hasSkor}
            onClick={toggleFinancialStudy}
          />
        </div>

        <hr
          style={{
            margin: "20px 0",
            border: "none",
            borderTop: "1px solid #eee",
            opacity: 1,
          }}
        />

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={handleNext}
            disabled={loading}
            style={{
              background: "#1e3733",
              color: "#fff",
              border: "none",
              borderRadius: 14,
              padding: "12px 20px",
              minHeight: 44,
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            {_t("startCheck", "Start check")}
          </button>
        </div>
      </div>
    </div>
  );
};
export default AdditionalChecksModal;
